import type { KeyboardEvent } from "react";
import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";

interface Props {
  value: string;
  onChange: (v: string) => void;
  onSubmit: () => void;
  disabled: boolean;
  onStop?: () => void;
}

export interface InputBoxHandle {
  focus: () => void;
}

export const InputBox = forwardRef<InputBoxHandle, Props>(function InputBox(
  { value, onChange, onSubmit, disabled, onStop },
  handleRef,
) {
  const ref = useRef<HTMLTextAreaElement>(null);
  const prevDisabledRef = useRef<boolean>(disabled);

  useImperativeHandle(handleRef, () => ({
    focus: () => ref.current?.focus(),
  }));

  // auto-resize
  useEffect(() => {
    if (ref.current) {
      ref.current.style.height = "auto";
      ref.current.style.height = `${Math.min(ref.current.scrollHeight, 240)}px`;
    }
  }, [value]);

  // Quando o assistente termina (disabled true→false), devolve foco pro textarea
  useEffect(() => {
    const wasDisabled = prevDisabledRef.current;
    prevDisabledRef.current = disabled;
    if (wasDisabled && !disabled && ref.current) {
      ref.current.focus();
    }
  }, [disabled]);

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      onSubmit();
    }
  };

  return (
    <form
      className="input-form"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <textarea
        ref={ref}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        placeholder={disabled ? "Respondendo…" : "Faça uma pergunta..."}
        rows={1}
        disabled={disabled}
        autoFocus
      />
      {disabled && onStop ? (
        <button
          type="button"
          className="stop-btn"
          onClick={onStop}
          aria-label="Parar resposta"
          title="Parar resposta (Esc)"
        >
          <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor" aria-hidden>
            <rect x="3.5" y="3.5" width="9" height="9" rx="1.5" />
          </svg>
        </button>
      ) : (
        <button type="submit" disabled={disabled || !value.trim()} aria-label="Enviar">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M8 2v12M8 2l-4 4M8 2l4 4" />
          </svg>
        </button>
      )}
    </form>
  );
});
